import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { PawPrint, ArrowRight, Search } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ScanPage() {
  const navigate = useNavigate();
  const [tagCode, setTagCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const checkTagAndRedirect = useCallback(async (code) => {
    setLoading(true);
    setError('');
    const upper = code.toUpperCase();
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tags/${upper}/check`);
      localStorage.setItem('saferpaws_tag_code', upper);
      if (response.data.activated && response.data.pet_id) {
        navigate(`/pet/${response.data.pet_id}`, { replace: true });
      } else {
        navigate(`/activate/${upper}`, { replace: true });
      }
    } catch {
      localStorage.setItem('saferpaws_tag_code', upper);
      navigate(`/activate/${upper}`, { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Check if we have a remembered tag code
  useEffect(() => {
    const savedCode = localStorage.getItem('saferpaws_tag_code');
    if (savedCode) {
      checkTagAndRedirect(savedCode);
    }
  }, [checkTagAndRedirect]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!tagCode.trim()) {
      setError('Please enter the code from your tag');
      return;
    }
    checkTagAndRedirect(tagCode.trim());
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-20" data-testid="scan-page">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-2xl border border-border shadow-lg p-8">
            {/* Logo */}
            <div className="flex items-center justify-center gap-2 mb-8">
              <PawPrint className="w-10 h-10 text-primary" />
              <span className="text-2xl font-heading font-bold text-primary">SaferPaws</span>
            </div>

            <h1 className="text-2xl font-heading font-bold text-foreground text-center mb-2">
              Welcome!
            </h1>
            <p className="text-muted-foreground text-center mb-8">
              Enter the code printed on your SaferPaws tag
            </p>

            {/* Tag Code Visual */}
            <div className="bg-secondary rounded-xl p-4 mb-6">
              <div className="flex items-center justify-center gap-3">
                <div className="w-16 h-16 bg-muted rounded-lg border-2 border-dashed border-primary/30 flex items-center justify-center">
                  <span className="text-xs text-muted-foreground text-center">Tag<br/>Code</span>
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-foreground">Look for a code like:</p>
                  <p className="text-lg font-mono font-bold text-primary">SP001</p>
                  <p className="text-xs text-muted-foreground">Printed on your tag</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="tagCode">Tag Code</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="tagCode"
                    value={tagCode}
                    onChange={(e) => {
                      setTagCode(e.target.value.toUpperCase());
                      setError('');
                    }}
                    placeholder="e.g., SP001"
                    className="pl-10 text-center text-lg font-mono uppercase"
                    maxLength={10}
                    data-testid="tag-code-input"
                  />
                </div>
                {error && (
                  <p className="text-sm text-destructive mt-1">{error}</p>
                )}
              </div>

              <Button 
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 py-6"
                disabled={loading}
                data-testid="continue-btn"
              >
                {loading ? 'Checking...' : 'Continue'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </form>

            <p className="mt-6 text-center text-xs text-muted-foreground">
              Don&apos;t have a tag yet?{' '}
              <a href="/shop" className="text-primary hover:underline">
                Buy one here
              </a>
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
